import { action, computed, type IReactionDisposer, makeObservable, observable, reaction } from "mobx";
import { newShortGuid } from "../util/types/guid";
import { type FileUpload, UploadStatus } from "./FileUpload";
import { DicomTags } from "../network/contracts/DicomDataSet";
import _ from "lodash";
import { parseDate } from "../util/date/parseDate";
import { formatOrDefault } from "../util/date/formatOrDefault";
import type { IUploadStore } from "./UploadStore";
import { StudyInUpload } from "./StudyInUpload";
import { mailRegex } from "../util/regex/regex";
import type { IPatientUploadCompleteCallbackStore } from "./PatientUploadCompleteCallbackStore";
import type { IUploadCodeAndSourceSelectionStore } from "./content/upload-code-and-source-selection/UploadCodeAndSourceSelectionStore";
import { throwError } from "../util/assertions/throwError";
import { delay } from "../util/functions/delay";
import { isNullOrWhiteSpace } from "../util/functions/isNullOrWhiteSpace";
import type { IDicomUploadTusFingerprintStore } from "./DicomUploadTusFingerprintStore";

export interface IPatientToUpload {
    patientGuid: string;
    filesToUpload: FileUpload[];
    readyToUpload: boolean;
    patientName: string;
    patientDobFormatted: string;
    patientId: string;
    studies: StudyInUpload[];
    status: UploadStatus;
    canBeRemoved: boolean;
    totalUploadedInPercentage: number;
    totalUploadedInBytes: number;
    filesToUploadTotalSize: number;
    numberOfFilesDoneUploading: number;
    reachedMaxToUploadSize: boolean;
    emailAddress: string;
    emailAddressIsValid: boolean;
    mobilePhone: string;
    privacyPolicyConsent: boolean;
    patientConsent: boolean;

    setEmailAddress(emailAddress: string): void;

    setMobilePhone(mobilePhone: string): void;

    addFiles(files: FileUpload[]): void;

    removeFile(file: FileUpload): void;

    removeStudyInUpload(study: StudyInUpload): void;

    startQueueingUpload(): void;

    pauseUpload(): void;

    resumeUpload(): void;
}

export class PatientToUpload implements IPatientToUpload {
    public patientGuid: string;

    @observable
    public filesToUpload: FileUpload[] = [];

    @observable
    public emailAddress = "";

    @observable
    public mobilePhone = "";

    @observable
    public patientConsent = false;

    @observable
    public privacyPolicyConsent = false;

    private _queueOrStartedChangedReaction: IReactionDisposer | null = null;

    private _statusChangedReaction: IReactionDisposer | null = null;

    private static readonly RetryPolicy: number[] = [1, 2, 4, 8];

    constructor(
        filesToUpload: FileUpload[],
        private readonly uploadStore: IUploadStore,
        private readonly patientUploadCompleteCallbackStore: IPatientUploadCompleteCallbackStore,
        private readonly uploadCodeAndSourceSelectionStore: IUploadCodeAndSourceSelectionStore,
        private readonly dicomUploadTusFingerprintStore: IDicomUploadTusFingerprintStore,
    ) {
        makeObservable(this);

        this.filesToUpload = filesToUpload;
        this.patientGuid = newShortGuid();

        this._queueOrStartedChangedReaction = reaction(
            () => [this.numberOfQueuedFiles, this.numberOfStartedFiles],
            () => this.checkQueuedUploads(),
        );

        this._statusChangedReaction = reaction(
            () => [this.status],
            async () => await this.statusChanged(),
        );
    }

    dispose(): void {
        if (this._queueOrStartedChangedReaction != null) {
            this._queueOrStartedChangedReaction();
            this._queueOrStartedChangedReaction = null;
        }

        if (this._statusChangedReaction != null) {
            this._statusChangedReaction();
            this._statusChangedReaction = null;
        }
    }

    @computed
    private get numberOfQueuedFiles(): number {
        return this.filesToUpload.filter((f) => f.status === UploadStatus.Queued).length;
    }

    @computed
    private get numberOfStartedFiles(): number {
        return this.filesToUpload.filter((f) => f.status === UploadStatus.Started).length;
    }

    @computed
    public get readyToUpload(): boolean {
        if (this.filesToUpload.length === 0) return false; //No uploadable files selected
        if (this.reachedMaxToUploadSize) return false; //Reached max upload size
        if (this.status === UploadStatus.Started) return false; //Already started
        if (this.status === UploadStatus.Paused) return false; //Paused
        if (this.status === UploadStatus.Done) return false; //Already done
        if (!this.privacyPolicyConsent || !this.patientConsent) return false;

        return true;
    }

    @computed
    public get patientName(): string {
        if (!this.filesToUpload || this.filesToUpload.length === 0) return "";
        return this.filesToUpload[0].getDicomTagValue(DicomTags.PatientName) ?? "";
    }

    @computed
    private get patientDob(): string {
        if (!this.filesToUpload || this.filesToUpload.length === 0) return "";
        return this.filesToUpload[0].getDicomTagValue(DicomTags.PatientBirthDate) ?? "";
    }

    @computed
    public get patientDobFormatted(): string {
        const patientDob = this.patientDob;
        const date = parseDate(patientDob, "yyyyMMdd");
        return formatOrDefault(date, "dd/MM/yyyy", patientDob);
    }

    @computed
    public get patientId(): string {
        if (!this.filesToUpload || this.filesToUpload.length === 0) return "";
        return this.filesToUpload[0].getDicomTagValue(DicomTags.PatientID) ?? "";
    }

    @computed
    public get studies(): StudyInUpload[] {
        const studies: StudyInUpload[] = [];
        const groupedFiles = _.groupBy(this.filesToUpload, (f) => f.studyId);

        for (const key in groupedFiles) {
            const files = groupedFiles[key];
            studies.push(new StudyInUpload(files));
        }

        return studies;
    }

    @computed
    public get status(): UploadStatus {
        //TOP to DOWN, at least one file with this status
        if (this.filesToUpload.find((u) => u.status === UploadStatus.MaliciousContent)) {
            return UploadStatus.MaliciousContent;
        }
        if (this.filesToUpload.find((u) => u.status === UploadStatus.NotADicomFile)) {
            return UploadStatus.NotADicomFile;
        }
        if (this.filesToUpload.find((u) => u.status === UploadStatus.Failed)) {
            return UploadStatus.Failed;
        }
        if (this.filesToUpload.find((u) => u.status === UploadStatus.Cancelled)) {
            return UploadStatus.Cancelled;
        }
        if (this.filesToUpload.find((u) => u.status === UploadStatus.RateLimited)) {
            return UploadStatus.RateLimited;
        }
        if (this.filesToUpload.find((u) => u.status === UploadStatus.ExceededUploadSizeLimit)) {
            return UploadStatus.ExceededUploadSizeLimit;
        }
        if (this.filesToUpload.find((u) => u.status === UploadStatus.IpBlocked)) {
            return UploadStatus.IpBlocked;
        }
        if (this.filesToUpload.find((u) => u.status === UploadStatus.UploadCodeExpired)) {
            return UploadStatus.UploadCodeExpired;
        }
        if (this.filesToUpload.find((u) => u.status === UploadStatus.Paused)) {
            return UploadStatus.Paused;
        }
        if (this.filesToUpload.find((u) => u.status === UploadStatus.Started)) {
            return UploadStatus.Started;
        }
        if (this.filesToUpload.find((u) => u.status === UploadStatus.Queued)) {
            return UploadStatus.Queued;
        }
        if (this.filesToUpload.find((u) => u.status === UploadStatus.ValidatingFiles)) {
            return UploadStatus.ValidatingFiles;
        }
        if (this.filesToUpload.find((u) => u.status === UploadStatus.Done)) {
            return UploadStatus.Done;
        }
        return UploadStatus.NotStarted;
    }

    @computed
    public get canBeRemoved(): boolean {
        if (this.status === UploadStatus.NotStarted) return true; //Can only be removed when upload has not started yet
        return false;
    }

    @computed
    public get totalUploadedInPercentage() {
        return (this.totalUploadedInBytes / this.filesToUploadTotalSize) * 100;
    }

    @computed
    public get totalUploadedInBytes() {
        let totalBytesUploaded = 0;

        this.filesToUpload.forEach((f) => {
            totalBytesUploaded += f.bytesUploaded;
        });

        return totalBytesUploaded;
    }

    @computed
    public get filesToUploadTotalSize(): number {
        let filesToUploadTotalSize = 0;

        this.filesToUpload.forEach((f) => {
            filesToUploadTotalSize += f.size;
        });

        return filesToUploadTotalSize;
    }

    @computed
    public get numberOfFilesDoneUploading(): number {
        return this.filesToUpload.filter((f) => f.arrivedAtTheBackend).length;
    }

    @computed
    public get reachedMaxToUploadSize(): boolean {
        return this.filesToUploadTotalSize > this.uploadStore.maxUploadSize;
    }

    @computed
    public get emailAddressIsValid(): boolean {
        if (this.emailAddress.trim() === "") return true;
        return this.emailAddress.match(mailRegex) !== null;
    }

    private checkQueuedUploads(): void {
        //Upload is paused or cancelled, don't check
        if (this.status === UploadStatus.Paused || this.status === UploadStatus.Cancelled) {
            return;
        }

        //Already at max uploading number of started files
        if (this.numberOfStartedFiles >= this.uploadStore.maxUploadFilesAtOnce) {
            return;
        }

        //Start number of queued files
        this.filesToUpload
            .filter((f) => f.status === UploadStatus.Queued)
            ?.slice(0, this.uploadStore.maxUploadFilesAtOnce - this.numberOfStartedFiles)
            .forEach((f) => {
                f.startUpload(this.patientGuid);
            });
    }

    private async statusChanged(): Promise<void> {
        if (this.status === UploadStatus.ValidatingFiles) {
            await this.startValidationProcess();
        }
    }

    private async startValidationProcess(): Promise<void> {
        await this.pollValidationStatusAsync();

        let retryPolicyIndex = 0;

        while (this.status === UploadStatus.ValidatingFiles) {
            await delay(1000 * PatientToUpload.RetryPolicy[retryPolicyIndex]);

            await this.pollValidationStatusAsync();

            if (retryPolicyIndex < PatientToUpload.RetryPolicy.length - 1) {
                retryPolicyIndex++;
            }
        }
    }

    private async pollValidationStatusAsync(): Promise<void> {
        const {uploadCodeAndSourceSelectionStore, dicomUploadTusFingerprintStore } = this;

        for (const study of this.studies) {
            
            const uploadDict: Map<string, FileUpload> = new Map<string, FileUpload>();
            
            study.filesToUpload.forEach((fu) => uploadDict.set(fu.uploadFingerprint, fu));

            const { patientUploadCompleteCallbackStore } = this;

            try {
                const completeUploadResult = await patientUploadCompleteCallbackStore.completeUpload(
                    study.filesToUpload.map(_ => _.uploadFingerprint),
                    uploadCodeAndSourceSelectionStore.selectedSourceForUpload?.id ?? throwError("Source is required"),
                    uploadCodeAndSourceSelectionStore.uploadCode,
                    isNullOrWhiteSpace(this.emailAddress) ? null : this.emailAddress,
                    isNullOrWhiteSpace(this.mobilePhone) ? null : this.mobilePhone,
                    isNullOrWhiteSpace(study.comment) ? null : study.comment,
                );

                const { maliciousFiles, notDicomFiles, successFiles } = completeUploadResult;

                for (const maliciousFile of maliciousFiles) {
                    uploadDict.get(maliciousFile)?.maliciousContentFound();
                }

                for (const notADicomFile of notDicomFiles) {
                    uploadDict.get(notADicomFile)?.notADicomFile();
                }

                for (const successFile of successFiles) {
                    uploadDict.get(successFile)?.uploadComplete();
                }

                dicomUploadTusFingerprintStore.clearFingerprintsFromLocalStorage();

            } catch (error) {
                if (error.message.includes("ExceededUploadSizeLimit")) {
                    for (const fileUpload of study.filesToUpload) {
                        fileUpload.uploadExceededUploadSizeLimit();
                    }
                } else {
                    console.error("Something went wrong during the upload complete callback: ", error);
                    for (const fileUpload of study.filesToUpload) {
                        fileUpload.uploadFailed();
                    }
                }
            }
        }
    }

    private checkIfThereAreStillFilesToUpload() {
        if (this.filesToUpload.length === 0) {
            this.uploadStore.removePatientToUpload(this);
        }
    }

    @action
    public setEmailAddress(emailAddress: string): void {
        this.emailAddress = emailAddress;
        this.filesToUpload.forEach((f) => {
            f.emailAddress = this.emailAddress;
        });
    }

    @action
    public setMobilePhone(mobilePhone: string): void {
        this.mobilePhone = mobilePhone;
        this.filesToUpload.forEach((f) => {
            f.mobilePhone = this.mobilePhone;
        });
    }

    @action
    public addFiles(files: FileUpload[]): void {
        for (const file of files) {
            if (!this.filesToUpload.find((f) => f.file.name === file.file.name && f.file.size === file.file.size)) {
                this.filesToUpload.push(file);
            }
        }
    }

    @action
    public removeFile(file: FileUpload): void {
        this.filesToUpload = this.filesToUpload.filter((f) => f.fileGuid !== file.fileGuid);
        this.checkIfThereAreStillFilesToUpload();
    }

    @action
    public removeStudyInUpload(study: StudyInUpload): void {
        const studyFilesGuids = study.filesToUpload.map((f) => f.fileGuid);
        this.filesToUpload = this.filesToUpload.filter((f) => !studyFilesGuids.includes(f.fileGuid));
        this.checkIfThereAreStillFilesToUpload();
    }

    @action
    public setPrivacyPolicyConsent(value: boolean): void {
        this.privacyPolicyConsent = value;
    }

    @action
    public setPatientConsent(value: boolean): void {
        this.patientConsent = value;
    }

    public startQueueingUpload(): void {
        if (this.filesToUpload.length === 0) return;
        if (this.reachedMaxToUploadSize) return;

        this.filesToUpload
            .filter((f) => f.status !== UploadStatus.Done)
            .forEach((f) => {
                f.queueUpload();
            });
    }

    public pauseUpload(): void {
        if (this.filesToUpload.length === 0) return;
        this.filesToUpload
            .filter((f) => f.status === UploadStatus.Started)
            .forEach((f) => {
                f.pauseUpload();
            });
    }

    public cancelUpload(): void {
        if (this.filesToUpload.length === 0) return;
        this.filesToUpload
            .filter((f) => f.status === UploadStatus.Started || f.status === UploadStatus.Queued)
            .forEach((f) => {
                f.cancelUpload();
            });
    }

    public resumeUpload(): void {
        //Coming from paused upload
        if (this.filesToUpload.length === 0) return;
        this.filesToUpload
            .filter((f) => f.status === UploadStatus.Paused)
            .forEach((f) => {
                f.queueUpload();
            });
    }
}
